import { useState } from 'react';
import { DatePicker, Tag } from 'antd';
import dayjs from 'dayjs';
import { useLazyExportProvisionalSalesXlsxQuery, useProvisionalSalesReportQuery } from '@/entities/reports';
import type { ProvisionalSalesReportRow } from '@/entities/reports';
import { ReportPage } from '@/shared/ui';
import type { ResponsiveColumns } from '@/shared/ui';
import { useBreakpoint } from '@/shared/lib/hooks';
import { useStoreContext } from '@/shared/lib/store-context';
import { todayInTz, formatDate } from '@/shared/lib/date';

/** Телефон: вместо восьми столбцов — карточка: товар, метка, документ (вид · номер · дата) и цифры строками. */
const MOBILE_COLUMNS: ResponsiveColumns<ProvisionalSalesReportRow> = [
  { title: 'Товар', dataIndex: 'categoryName', mobile: 'title' },
  {
    title: '',
    mobile: 'subtitle',
    render: (_v, r) => (r.noPurchasePrice ? <Tag color="error">нет закупочной цены</Tag> : <Tag color="warning">предварительная</Tag>),
  },
  { title: 'Документ', mobile: 'subtitle', render: (_v, r) => `${r.typeLabel} · ${r.number} · ${formatDate(r.docDate)}` },
  { title: 'Штук', dataIndex: 'qtyPcs' },
  { title: 'Без партии, шт', dataIndex: 'qtyUncovered' },
  { title: 'Себестоимость', dataIndex: 'costAmount', render: (v: string | null) => v ?? '—' },
];

/** §13, §8.6: строки с предварительной себестоимостью за период. */
export function ProvisionalSalesReportPage() {
  const ctx = useStoreContext();
  const { isMobile } = useBreakpoint();
  const today = todayInTz(ctx.timezone);
  const defaultFrom = dayjs(today).startOf('month').format('YYYY-MM-DD');
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(today);
  const periodChanged = from !== defaultFrom || to !== today;
  const { data, isFetching } = useProvisionalSalesReportQuery({ from, to }, { pollingInterval: 30_000 });
  const [exportXlsx] = useLazyExportProvisionalSalesXlsxQuery();

  const columns: ResponsiveColumns<ProvisionalSalesReportRow> = [
    { title: 'Документ', dataIndex: 'typeLabel', width: 180 },
    { title: 'Номер', dataIndex: 'number', width: 110 },
    { title: 'Дата', dataIndex: 'docDate', width: 110, render: (v: string) => formatDate(v) },
    { title: 'Товар', dataIndex: 'categoryName' },
    { title: 'Штук', dataIndex: 'qtyPcs', width: 90, align: 'right' },
    { title: 'Без партии, шт', dataIndex: 'qtyUncovered', width: 130, align: 'right' },
    { title: 'Себестоимость', dataIndex: 'costAmount', width: 130, align: 'right', render: (v: string | null) => v ?? '—' },
    {
      title: '',
      width: 160,
      render: (_v, r) => (r.noPurchasePrice ? <Tag color="error">нет закупочной цены</Tag> : <Tag color="warning">предварительная</Tag>),
    },
  ];

  return (
    <ReportPage
      title="Продажи с предварительной себестоимостью"
      filters={
        <DatePicker.RangePicker
          format="DD.MM.YYYY"
          inputReadOnly={isMobile}
          value={[dayjs(from), dayjs(to)]}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setFrom(dates[0].format('YYYY-MM-DD'));
              setTo(dates[1].format('YYYY-MM-DD'));
            }
          }}
        />
      }
      columns={isMobile ? MOBILE_COLUMNS : columns}
      activeFilters={periodChanged ? 1 : 0}
      onResetFilters={() => {
        setFrom(defaultFrom);
        setTo(today);
      }}
      filtersSummary={`${formatDate(from)} — ${formatDate(to)}`}
      data={data?.rows ?? []}
      loading={isFetching}
      rowKey={(r) => `${r.documentId}-${r.categoryId}`}
      onExport={() => exportXlsx({ from, to }).unwrap()}
      exportFilename="Предварительная себестоимость.xlsx"
    />
  );
}
